import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton, DetailCard, FieldRow } from "../../Components/Needed";
import NameGenderModal from "./NameGenderModal";
import RejectReasonModal from "./RejectReasonModal";
import CustomerBookings from "./CustomerBookings";
import KycReviewPanel from "./KycReviewPanel";
import KycLogPanel from "./KycLogPanel";
import TestModeCard from "./TestModeCard";
import LinkedCustomerCard from "../LinkedCustomer/LinkedCustomerCard"; // TEMPORARY (temp/linked-customer)
import CustomerVerification from "./CustomerVerification";
import Timeline from "../../Components/Timeline";
import Profile from "../../Assets/images/avatar.png";
import {
    FaArrowLeft, FaAddressCard, FaExclamation, FaIdCardAlt, FaPassport, FaPhoneAlt, FaRegUserCircle,
    FaClock, FaTimesCircle, FaMinusCircle, FaHourglassHalf, FaBan, FaPen, FaUserSlash, FaUserCheck,
    FaSearchPlus, FaExternalLinkAlt, FaCheckCircle, FaGenderless,
} from "react-icons/fa";
import { BsFillPatchCheckFill, BsFillPatchExclamationFill } from "react-icons/bs";
import { IoIosMail, IoIosMale, IoIosFemale } from "react-icons/io";
import { MdAddTask, MdCancel, MdOutlineDirectionsBike } from "react-icons/md";
import { IoLocation, IoShieldCheckmarkOutline, IoCalendarOutline } from "react-icons/io5";
import { LuMailCheck, LuMailWarning } from "react-icons/lu";
import { ImBin } from "react-icons/im";
import { LiaCarSideSolid } from "react-icons/lia";
import AcceptModal from "./AcceptModal";
import { listBackPath } from "../../Helpers/useListParams";



const Badge = ({ text, icon, className = "" }) => (
    <span className={`custom-badge text-capitalize ${className}`}>
        {icon} {text}
    </span>
);

const SectionHeading = ({ icon, title, action }) => (
    <div className="p-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="fw-semibold d-flex align-items-center gap-2 mb-0">
                <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary-light flex-shrink-0" style={{ width: 32, height: 32 }}>
                    {icon}
                </span>
                {title}
            </h5>
            {action || null}
        </div>
        <hr className="m-0 mt-3" />
    </div>
);
const KYC_STATUS_META = {
    waiting: { label: "Waiting", badgeClass: "bg-dark", dotClass: "waiting", Icon: FaHourglassHalf },
    not_submitted: { label: "Not Submitted", badgeClass: "bg-secondary", dotClass: "not-submitted", Icon: FaMinusCircle },
    pending: { label: "Pending", badgeClass: "bg-warning text-dark", dotClass: "pending", Icon: FaClock },
    verified: { label: "Verified", badgeClass: "bg-success", dotClass: "verified", Icon: FaCheckCircle },
    rejected: { label: "Rejected", badgeClass: "bg-danger", dotClass: "rejected", Icon: FaTimesCircle },
    expired: { label: "Expired", badgeClass: "bg-secondary", dotClass: "expired", Icon: FaBan },
};
const statusMeta = (status) => KYC_STATUS_META[status] || KYC_STATUS_META.not_submitted;

const StatusPillLabel = ({ status }) => {
    const meta = statusMeta(status);
    const Icon = meta.Icon;
    return (
        <span className={`badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1 ${meta.badgeClass}`}>
            <Icon size={11} /> {meta.label}
        </span>
    );
};

// Opens the KYC stage modal by code. The old data-bs-toggle attribute opened it even when the click was
// refused ("already verified"), so the modal appeared with nothing to do.
const openStageModal = () => {
    const modalEl = document.getElementById("stageModal");
    if (modalEl && window.bootstrap) window.bootstrap.Modal.getOrCreateInstance(modalEl).show();
};

const KycCard = ({ title, icon, onClick, status, subtitle, canSubmit = true }) => {
    const meta = statusMeta(status);
    const isOpen = status === "verified" || status === "pending" || status === "waiting";
    const canOpen = !isOpen && canSubmit;
    const activate = () => {
        const accepted = onClick?.();
        if (accepted !== false) openStageModal();
    };

    return (
        <div className="col">
            <div
                className={`kyc-card text-center p-3 rounded-3 ${canOpen ? "cursor-pointer" : ""}`}
                role="button"
                tabIndex={0}
                onClick={canOpen ? activate : undefined}
                onKeyDown={(e) => { if (canOpen && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); activate(); } }}
                aria-label={`${title}: ${meta.label}${!isOpen && !canSubmit ? " (no permission to submit)" : ""}`}
                title={!isOpen && !canSubmit ? "You don't have permission to submit this document" : undefined}
                style={!isOpen && !canSubmit ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
            >
                <div className={`kyc-icon ${meta.dotClass}`}>
                    {icon}
                </div>
                <div className="fw-semibold mt-2">
                    {title}
                </div>
                <span className={`badge mt-1 d-inline-flex align-items-center gap-1 ${meta.badgeClass}`}>
                    <meta.Icon size={10} /> {meta.label}
                </span>
                {subtitle ? <div className="kyc-tile-subtitle mt-1">{subtitle}</div> : <div className="kyc-tile-subtitle mt-1">-</div>}
            </div>
        </div>
    );
};

const StatTile = ({ icon, value, label, tone = "primary" }) => (
    <div className="col-6 col-md-3">
        <div className="stat-tile h-100">
            <span className={`stat-tile-icon stat-tile-icon-${tone}`}>{icon}</span>
            <div className="stat-tile-value">{value}</div>
            <div className="stat-tile-label">{label}</div>
        </div>
    </div>
);

const VerificationDetails = ({ status, submittedAt, reviewedAt, reviewedBy, reviewerRole, reason }) => (
    <div className="verify-details">
        <div className="verify-status-row">
            <span className="text-muted small">Verification Status</span>
            <StatusPillLabel status={status} />
        </div>
        <div className="d-flex flex-column gap-3 mt-3">
            <FieldRow label="Submitted" value={submittedAt ? formatDateTime(submittedAt) : "Not submitted"} />
            <FieldRow label="Reviewed" value={reviewedAt ? formatDateTime(reviewedAt) : "Not reviewed"} />
            <FieldRow label="Reviewer" value={reviewedBy} />
            <FieldRow label="Reviewer Role" value={reviewerRole} />
        </div>
        {reason ? (
            <div className="alert alert-danger border-0 rounded-3 mt-3 mb-0 d-flex gap-2">
                <FaExclamation className="mt-1 flex-shrink-0" />
                <div>
                    <div className="fw-semibold mb-1">Rejection Reason</div>
                    <div className="small">{reason}</div>
                </div>
            </div>
        ) : null}
    </div>
);

const VerificationActions = ({ status, onVerify, onReject, show = true, disabled }) => {
    if (status !== "pending" || !show) return null;
    return (
        <div className="border-top mt-3 pt-3 d-flex gap-2">
            <button type="button" className="btn btn-success flex-fill d-inline-flex align-items-center justify-content-center gap-2" disabled={disabled} onClick={onVerify}>
                <FaCheckCircle /> Verify
            </button>
            <button type="button" className="btn btn-outline-danger flex-fill d-inline-flex align-items-center justify-content-center gap-2" disabled={disabled} onClick={onReject}>
                <FaTimesCircle /> Reject
            </button>
        </div>
    );
};

const DocFrame = ({ label, src, alt, onZoom, onOpen, missingText = "Document not uploaded" }) => (
    <div className="doc-frame h-100">
        <div className="doc-frame-label">{label}</div>
        {src ? (
            <>
                <div className="doc-frame-body">
                    <img src={src} alt={alt} onClick={onZoom} />
                </div>
                <div className="doc-frame-actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={onZoom}>
                        <FaSearchPlus size={12} /> Zoom
                    </button>
                    {onOpen ? (
                        <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={onOpen}>
                            <FaExternalLinkAlt size={12} /> Open
                        </button>
                    ) : null}
                </div>
            </>
        ) : (
            <div className="doc-frame-body doc-frame-missing">
                <div className="text-center text-muted">
                    <FaMinusCircle size={20} className="d-block mx-auto mb-2" />
                    <small>{missingText}</small>
                </div>
            </div>
        )}
    </div>
);

const CustomerDetail = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [customer, setCustomer] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stage, setStage] = useState("")
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [showNameGender, setShowNameGender] = useState(false);
    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [activateOpen, setActivateOpen] = useState(false);
    const [deactivateReason, setDeactivateReason] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const [rejectPrompt, setRejectPrompt] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const canViewTimeline = hasPermission("customer.timeline");
        const [customerResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/staff/customer/${customerId}` }),
            canViewTimeline
                ? Handler({ method: "GET", url: `/staff/customer/${customerId}/log`, params: { limit: 20 } })
                : Promise.resolve(null),
        ]);
        setCustomer(customerResponse?.success ? customerResponse.data.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [customerId, hasPermission]);

    // Opening a different customer must not flash the previous one while loading.
    useEffect(() => { setCustomer(null); setLogs([]); }, [customerId]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!lightbox) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox]);

    const openInNewTab = (url) => { if (url) window.open(url, "_blank", "noopener,noreferrer"); };

    const changeStatus = async (status, reason) => {
        setStatusUpdating(true);
        const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/status`, data: { status, reason } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            setDeactivateOpen(false);
            setActivateOpen(false)
            setDeactivateReason("");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setStatusUpdating(false);
    };

    const STAGE_LABELS = { profile: "Profile", email: "Email", aadhaar: "Aadhaar", dl: "Driving License", third: "Passport/Voter" };
    const STAGE_CATEGORY = { profile: "profile", email: "email", aadhaar: "aadhaar", dl: "dl", third: "thirdDocument" };
    const changeVerificationStage = (stageKey) => {
        const categoryKey = STAGE_CATEGORY[stageKey];
        const alreadyApproved = categoryKey ? !!customer?.categories?.[categoryKey]?.approved : false;
        if (alreadyApproved) {
            toast.error(`${STAGE_LABELS[stageKey] || textCapitalize(stageKey)} is already verified`);
            return false;
        }
        setStage(stageKey);
        return true;
    };

    const profilePhotoVerify = async () => {
        setActionLoading(true)
        const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/photo/review`, data: { toStatus: "verified" } });
        if (response?.success) {
            toast.success(response?.data?.message || "Profile photo verified");
            load();
        } else {
            toast.error(response?.data?.message || "Unable to verify profile photo");
        }
        setActionLoading(false)
    }

    const profilePhotoReject = () => {
        setRejectPrompt({
            title: "Reject profile photo",
            onSubmit: async (reason) => {
                const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/photo/review`, data: { toStatus: "rejected", reason } });
                if (response?.success) {
                    toast.success(response?.data?.message || "Profile photo rejected");
                    load();
                } else {
                    toast.error(response?.data?.message || "Unable to reject profile photo");
                }
            },
        });
    }

    const profilePhotoDelete = () => {
        setRejectPrompt({
            title: "Delete profile photo",
            description: "This permanently removes the customer's current profile photo. Please provide a reason for the record.",
            confirmLabel: "Delete photo",
            onSubmit: async (reason) => {
                const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/photo/reset`, data: { reason: reason } });
                if (response?.success) {
                    toast.success(response?.data?.message || "Profile photo deleted");
                    load();
                } else {
                    toast.error(response?.data?.message || "Unable to delete profile photo");
                }
            },
        });
    }

    const submitAadhaarReview = async (value, reason) => {
        setActionLoading(true)
        const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/kyc/aadhaar/review`, data: { toStatus: value, ...(value === "rejected" ? { reason: reason } : {}) } });
        if (response?.success) {
            toast.success(response?.data?.message || "Aadhaar rejected");
            load();
        } else {
            toast.error(response?.data?.message || "Unable to reject profile photo");
        }
        setActionLoading(false)
    }

    const aadhaarReviewVerify = (value) => {
        if (value === "rejected") {
            setRejectPrompt({ title: "Reject Aadhaar", onSubmit: (reason) => submitAadhaarReview(value, reason) });
            return;
        }
        submitAadhaarReview(value);
    }

    const submitDlReview = async (value, reason) => {
        setActionLoading(true)
        const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/kyc/dl/review`, data: { toStatus: value, ...(value === "rejected" ? { reason: reason } : {}) } });
        if (response?.success) {
            toast.success(response?.data?.message || "DL rejected");
            load();
        } else {
            toast.error(response?.data?.message || "Unable to reject profile photo");
        }
        setActionLoading(false)
    }

    const dlReviewVerify = (value) => {
        if (value === "rejected") {
            setRejectPrompt({ title: "Reject Driving License", onSubmit: (reason) => submitDlReview(value, reason) });
            return;
        }
        submitDlReview(value);
    }

    const thirdDocumentType = (() => {
        const voterSubmittedAt = customer?.kyc?.voter?.voterId ? customer?.kyc?.voter?.submittedAt : null;
        const passportSubmittedAt = customer?.kyc?.passport?.passportNo ? customer?.kyc?.passport?.submittedAt : null;
        if (voterSubmittedAt && passportSubmittedAt) {
            return new Date(passportSubmittedAt) > new Date(voterSubmittedAt) ? "passport" : "voter";
        }
        if (passportSubmittedAt) return "passport";
        if (voterSubmittedAt) return "voter";
        return null;
    })();

    const submitThirdDocumentReview = async (value, reason) => {
        setActionLoading(true)
        const response = await Handler({ method: "patch", url: `/staff/customer/${customerId}/kyc/third/review`, data: { docType: thirdDocumentType, toStatus: value, ...(value === "rejected" ? { reason: reason } : {}) } });
        if (response?.success) {
            toast.success(response?.data?.message || (value === "verified" ? "Third document approved" : "Third document rejected"));
            load();
        } else {
            toast.error(response?.data?.message || "Unable to update third document");
        }
        setActionLoading(false)
    }
    const thirdDocumentReviewVerify = (value) => {
        if (!thirdDocumentType) return;
        if (value === "rejected") {
            setRejectPrompt({
                title: `Reject ${thirdDocumentType === "voter" ? "Voter ID" : "Passport"}`,
                onSubmit: (reason) => submitThirdDocumentReview(value, reason),
            });
            return;
        }
        submitThirdDocumentReview(value);
    }

    // Only the very first load swaps the page for the skeleton. A refresh (after a verify / reject / edit)
    // keeps the page - and any open modal on it - mounted, so nothing is torn out from under a dialog.
    if (loading && !customer) {
        return (
            <div className="settings-card">
                <Skeleton height={64} className="mb-3 rounded-4" />
                <div className="d-flex gap-4 mb-4">
                    <Skeleton height={96} width={96} className="rounded-circle" />
                    <div className="flex-grow-1">
                        <Skeleton height={22} width="30%" className="mb-2" />
                        <Skeleton height={14} width="45%" className="mb-2" />
                        <Skeleton height={14} width="35%" />
                    </div>
                </div>
                <Skeleton height={140} className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="settings-card text-center py-5">
                <FaMinusCircle size={28} className="text-muted mb-3" />
                <p className="text-muted mb-3">Customer not found.</p>
                <button type="button" className="btn back-btn" onClick={() => navigate(listBackPath("/customers"))}>Back to customers</button>
            </div>
        );
    }

    const kycStatuses = {
        profile: customer?.photo?.status,
        email: customer?.email?.status ? "verified" : "not_submitted",
        aadhaar: customer?.kyc?.aadhaar?.status,
        dl: customer?.kyc?.dl?.status,
        third: thirdDocumentType === "voter" ? customer?.kyc?.voter?.status : thirdDocumentType === "passport" ? customer?.kyc?.passport?.status : "not_submitted",
    };
    const kycTotal = Object.keys(kycStatuses).length;
    const kycVerifiedCount = Object.values(kycStatuses).filter((s) => s === "verified").length;
    const kycRejectedCount = Object.values(kycStatuses).filter((s) => s === "rejected").length;
    const thirdDocumentLabel = thirdDocumentType === "voter" ? "Voter ID" : thirdDocumentType === "passport" ? "Passport" : "Voter/Passport";
    const genderMeta = customer?.gender === "male"
        ? { text: "Male", icon: <IoIosMale />, className: "badge-male" }
        : customer?.gender === "female"
            ? { text: "Female", icon: <IoIosFemale />, className: "badge-female" }
            : customer?.gender
                ? { text: "Others", icon: <FaGenderless />, className: "badge-transgender" }
                : null;

    return (
        <>
            <button type="button" className="btn back-btn text-decoration-none  mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/customers"))}>
                <FaArrowLeft /> Back to customers
            </button>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                <div className="cv-header-banner" />
                <div className="card-body pt-0">
                    <div className="row g-4">
                        <div className="col-12 col-lg-6">
                            <div className="d-flex flex-column flex-md-row gap-4" style={{ marginTop: -44 }}>
                                <div className={`rounded-circle flex-shrink-0 cv-avatar-ring ${customer?.verified ? "is-verified" : ""}`}>
                                    <img
                                        src={customer?.photo?.image || Profile}
                                        alt={customer?.firstName ? `${customer.firstName} ${customer?.lastName || ""}` : "Customer profile"}
                                        className="customer-profile-img rounded-circle"
                                        style={{ display: "block", objectFit: "cover" }}
                                    />
                                </div>

                                <div className="pt-md-5 flex-grow-1 min-w-0">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h3 className="fw-bold text-capitalize mb-1">
                                            {customer?.firstName || "Unnamed"}{" "}
                                            {customer?.lastName}
                                        </h3>
                                        {customer?.verified ? <BsFillPatchCheckFill className="text-success mb-1" size={18} title="Verified customer" /> : null}
                                        {
                                            customer?.verified && hasPermission("customer.edit") ? <button
                                                type="button"
                                                className="btn btn-sm btn-link text-muted p-0 mb-1"
                                                onClick={() => setShowNameGender(true)}
                                                title="Edit name & gender"
                                                aria-label="Edit name and gender"
                                            >
                                                <FaPen size={13} />
                                            </button> : ""
                                        }

                                    </div>

                                    <small className="text-muted d-block mb-3 font-monospace">
                                        #{customer?.customerId}
                                    </small>

                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {genderMeta ? <Badge text={genderMeta.text} icon={genderMeta.icon} className={genderMeta.className} /> : null}
                                        <Badge
                                            text={customer?.status ? "Active" : "Inactive"}
                                            icon={customer?.status ? <FaCheckCircle /> : <MdCancel />}
                                            className={customer?.status ? "badge-active" : "badge-inactive"}
                                        />
                                        <Badge
                                            text={customer?.verified ? "Verified" : "Not Verified"}
                                            icon={customer?.verified ? <BsFillPatchCheckFill /> : <BsFillPatchExclamationFill />}
                                            className={customer?.verified ? "badge-verified" : "badge-warning"}
                                        />
                                    </div>

                                    <div className="d-flex flex-column gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary-light flex-shrink-0" style={{ width: 30, height: 30 }}>
                                                <FaPhoneAlt className="text-primary" size={13} />
                                            </span>
                                            <span>+91 {customer?.phone}</span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary-light flex-shrink-0" style={{ width: 30, height: 30 }}>
                                                <IoIosMail className="text-primary" size={14} />
                                            </span>
                                            {customer?.email?.emailId ? <span className={customer?.email?.status ? "" : "text-danger"} >{customer?.email?.emailId}</span> : <span className="text-muted">Not mentioned</span>}
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary-light flex-shrink-0" style={{ width: 30, height: 30 }}>
                                                <IoCalendarOutline className="text-primary" size={14} />
                                            </span>
                                            <span>Joined {customer?.createdAt ? formatDateTime(customer.createdAt) : "-"}</span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2 text-capitalize">
                                            <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary-light flex-shrink-0" style={{ width: 30, height: 30 }}>
                                                <MdAddTask className="text-primary" size={14} />
                                            </span>
                                            <span className="text-gray">{customer?.createdBy?.type} — {customer?.createdBy?.name}</span>
                                        </div>
                                    </div>

                                    {
                                        !customer?.status && customer?.errorMsg ?
                                            <div className="mt-3 d-flex align-items-center gap-2 fs-7 text-danger px-3 py-2 rounded-3 cv-inactive-alert">
                                                <FaExclamation size={12} /> Reason: {customer?.errorMsg}
                                            </div>
                                            : ""
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-lg-6 mt-4 mt-lg-2 d-flex flex-column gap-3">
                            <div className="d-flex flex-column gap-2 mt-5 justify-content-center align-items-center">
                                {hasPermission("customer.status") ? (
                                    customer?.status ? (
                                        <button type="button" className="btn btn-outline-danger text-center" disabled={statusUpdating} onClick={() => setDeactivateOpen(true)}>
                                            <FaUserSlash /> Deactivate account
                                        </button>
                                    ) : (
                                        <button type="button" className="btn btn-success text-center" disabled={statusUpdating} onClick={() => setActivateOpen(true)}>
                                            <FaUserCheck /> Activate account
                                        </button>
                                    )
                                ) : null}
                            </div>
                            <div className="row g-3">
                                <StatTile icon={<IoShieldCheckmarkOutline />} value={`${kycVerifiedCount}/${kycTotal}`} label="KYC Verified" tone={kycVerifiedCount === kycTotal ? "success" : "primary"} />
                                <StatTile icon={<FaTimesCircle />} value={kycRejectedCount} label="Documents Rejected" tone={kycRejectedCount > 0 ? "danger" : "muted"} />
                                <StatTile icon={<FaCheckCircle />} value={`${customer?.submittedPercent || 0}%`} label="Submitted" tone="info" />
                                <StatTile icon={<BsFillPatchCheckFill />} value={`${customer?.approvedPercent || 0}%`} label="Approved" tone="success" />
                            </div>

                            {/* {
                                !customer?.verified &&
                                <div className="stats-wrapper p-3 rounded-4" style={{ background: "#F7F9FA", border: "1px solid #EEF1F3" }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="small text-muted">Verification progress</span>
                                        <span className="fw-semibold fs-7 cv-progress-pill">
                                            {customer?.submittedPercent || 0}%
                                        </span>
                                    </div>
                                    <div className="progress rounded-pill mt-2" style={{ height: 8 }}>
                                        <div className="progress-bar bg-success" style={{ width: `${customer?.submittedPercent || 0}%` }} />
                                    </div>
                                </div>
                            } */}
                        </div>
                    </div>
                </div>
            </div>

            {deactivateOpen ? (
                <div className="settings-card mb-4">
                    <label className="form-label small">Reason for deactivating this customer</label>
                    <textarea className="form-control mb-2" rows={2} value={deactivateReason} disabled={statusUpdating} onChange={(e) => setDeactivateReason(e.target.value)} />
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-danger" disabled={statusUpdating || !deactivateReason.trim()} onClick={() => changeStatus(false, deactivateReason)}>
                            Confirm deactivate
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={statusUpdating} onClick={() => { setDeactivateOpen(false); setDeactivateReason(""); }}>Cancel</button>
                    </div>
                </div>
            ) : ""}

            <div className="row">
                <div className="col-12 col-xl-8">

                    {/* Customer Statistics */}
                    {/* <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <SectionHeading icon={<IoShieldCheckmarkOutline className="text-primary" size={16} />} title="Customer Statistics" />
                        <div className="card-body pt-0">
                            <div className="row g-3">
                                <StatTile icon={<IoShieldCheckmarkOutline />} value={`${kycVerifiedCount}/${kycTotal}`} label="KYC Verified" tone={kycVerifiedCount === kycTotal ? "success" : "primary"} />
                                <StatTile icon={<FaTimesCircle />} value={kycRejectedCount} label="Documents Rejected" tone={kycRejectedCount > 0 ? "danger" : "muted"} />
                                <StatTile icon={<FaCheckCircle />} value={`${customer?.submittedPercent || 0}%`} label="Submitted" tone="info" />
                                <StatTile icon={<BsFillPatchCheckFill />} value={`${customer?.approvedPercent || 0}%`} label="Approved" tone="success" />
                            </div>
                        </div>
                    </div> */}

                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <SectionHeading
                            icon={<IoShieldCheckmarkOutline className="text-primary" size={16} />}
                            title="KYC Verification"
                            action={<span className="text-muted small fw-semibold">{kycVerifiedCount} / {kycTotal} Verified</span>}
                        />
                        <div className="card-body pt-0">
                            <div className="row g-3">
                                <KycCard
                                    title="Profile"
                                    onClick={() => changeVerificationStage("profile")}
                                    icon={<FaRegUserCircle />}
                                    status={kycStatuses.profile}
                                    subtitle={customer?.photo?.submittedAt ? formatDateTime(customer.photo.submittedAt) : null}
                                    canSubmit={hasPermission("customer.photo.manage")}
                                />
                                <KycCard
                                    title="Email"
                                    onClick={() => changeVerificationStage("email")}
                                    status={kycStatuses.email}
                                    icon={customer?.categories?.email?.approved ? <LuMailCheck /> : <LuMailWarning />}
                                    canSubmit={hasPermission("customer.email.manage")}
                                />
                                <KycCard
                                    title="Aadhaar"
                                    onClick={() => changeVerificationStage("aadhaar")}
                                    status={kycStatuses.aadhaar}
                                    icon={<FaAddressCard />}
                                    subtitle={customer?.kyc?.aadhaar?.submittedAt ? formatDateTime(customer.kyc.aadhaar.submittedAt) : null}
                                    canSubmit={hasPermission("customer.kyc.aadhaar.submit")}
                                />
                                <KycCard
                                    title="Driving License"
                                    onClick={() => changeVerificationStage("dl")}
                                    status={kycStatuses.dl}
                                    icon={<FaIdCardAlt />}
                                    subtitle={customer?.kyc?.dl?.submittedAt ? formatDateTime(customer.kyc.dl.submittedAt) : null}
                                    canSubmit={hasPermission("customer.kyc.dl.submit")}
                                />
                                <KycCard
                                    title={thirdDocumentLabel}
                                    onClick={() => changeVerificationStage("third")}
                                    status={kycStatuses.third}
                                    icon={<FaPassport />}
                                    subtitle={customer?.kyc?.voter?.submittedAt ? formatDateTime(customer.kyc.voter.submittedAt) : null}
                                    canSubmit={hasPermission("customer.kyc.voter.submit") || hasPermission("customer.kyc.passport.submit")}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Customer Photo */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <SectionHeading
                            icon={<FaRegUserCircle className="text-primary" size={16} />}
                            title="Customer Photo"
                            action={customer?.photo?.status === "verified" && hasPermission("customer.photo.manage") ? (
                                <button type="button" className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2" onClick={() => profilePhotoDelete(customerId)}>
                                    <ImBin /> Delete photo
                                </button>
                            ) : null}
                        />
                        <div className="card-body pt-0">
                            <div className="row g-3">
                                <div className="col-12 col-md-5">
                                    <DocFrame
                                        label="Profile Photo"
                                        src={customer?.photo?.image}
                                        alt="Customer profile"
                                        onZoom={() => setLightbox({ src: customer.photo.image, alt: "Customer profile photo" })}
                                        onOpen={() => openInNewTab(customer?.photo?.image)}
                                        missingText="Photo not uploaded"
                                    />
                                </div>
                                <div className="col-12 col-md-7">
                                    <VerificationDetails
                                        status={customer?.photo?.status}
                                        submittedAt={customer?.photo?.submittedAt}
                                        reviewedAt={customer?.photo?.reviewedAt}
                                        reviewedBy={customer?.photo?.reviewedBy}
                                        reviewerRole={customer?.photo?.reviewerRole}
                                        reason={customer?.photo?.reason}
                                    />
                                    <VerificationActions status={customer?.photo?.status} onVerify={profilePhotoVerify} onReject={profilePhotoReject} show={hasPermission("customer.photo.manage")} disabled={actionLoading} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Aadhaar Details */}
                    {customer?.kyc?.aadhaar?.aadhaarNo ? (
                        <DetailCard title="Aadhaar Details" icon={<FaAddressCard />}>
                            <div className="aadhaar-hero mb-4 p-4">
                                <div className="row align-items-center">
                                    {customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.profile_image && (
                                        <div className="col-md-3 text-center mb-3 mb-md-0">
                                            <img
                                                src={`data:image/jpeg;base64,${customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.profile_image}`}
                                                className="aadhaar-photo cursor-pointer"
                                                alt="Aadhaar"
                                                onClick={() => setLightbox({ src: `data:image/jpeg;base64,${customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.profile_image}`, alt: "Aadhaar photo" })}
                                            />
                                        </div>
                                    )}
                                    <div className="col-md-9" >
                                        <div className="info-grid" >
                                            <div className="info-field">
                                                <div className="info-field-label"> FULL NAME </div>
                                                <div className="info-field-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.full_name || "-"}</div>
                                            </div>
                                            <div className="info-field">
                                                <div className="info-field-label"> GENDER </div>
                                                <div className="info-field-value">
                                                    {customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.gender === "M" ? "Male"
                                                        : customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.gender === "F" ? "Female"
                                                            : customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.gender ? "Others" : "-"}
                                                </div>
                                            </div>
                                            <div className="info-field">
                                                <div className="info-field-label"> DATE OF BIRTH</div>
                                                <div className="info-field-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.dob || "-"}</div>
                                            </div>
                                            <div className="info-field">
                                                <div className="info-field-label"> MOBILE NUMBER </div>
                                                <div className="info-field-value">+91 {customer?.kyc?.aadhaar?.aadhaarDetails?.digilocker_metadata?.mobile_number || "-"}</div>
                                            </div>
                                            <div className="info-field">
                                                <div className="info-field-label"> AADHAAR NUMBER </div>
                                                <div className="info-field-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.masked_aadhaar || "-"}</div>
                                            </div>
                                            <div className="info-field">
                                                <div className="info-field-label"> YEAR OF BIRTH </div>
                                                <div className="info-field-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.yob || "-"}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-4">
                                <div className="col-lg-7">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body p-4">
                                            <h6 className="mb-3 d-flex align-items-center gap-2 fw-semibold">
                                                <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary-light flex-shrink-0" style={{ width: 30, height: 30 }}>
                                                    <IoLocation className="text-primary" size={15} />
                                                </span>
                                                Address Details
                                            </h6>
                                            <div className="address-display mb-4" >
                                                <h6>Full Address</h6>
                                                {
                                                    customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.full_address ?
                                                        <p className="m-0">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.full_address}</p>
                                                        :
                                                        <p className="m-0 text-muted">No address details available</p>
                                                }
                                            </div>
                                            <div className="info-grid px-1" >
                                                <div className="info-field">
                                                    <div className="info-field-label"> House Number </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.house || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> Street </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.street || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> Landmark </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.landmark || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> Locality </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.loc || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> Village/Town/City </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.vtc || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label">Sub District </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.subdist || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> District </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.dist || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> State </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.state || "-"}</div>
                                                </div>
                                                <div className="info-field">
                                                    <div className="info-field-label"> Pincode </div>
                                                    <div className="info-value">{customer?.kyc?.aadhaar?.aadhaarDetails?.aadhaar_xml_data?.zip || "-"}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-5">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body p-4">
                                            <h6 className="fw-semibold mb-3">Verification Details</h6>
                                            <VerificationDetails
                                                status={customer?.kyc?.aadhaar?.status}
                                                submittedAt={customer?.kyc?.aadhaar?.submittedAt}
                                                reviewedAt={customer?.kyc?.aadhaar?.reviewedAt}
                                                reviewedBy={customer?.kyc?.aadhaar?.reviewedBy}
                                                reviewerRole={customer?.kyc?.aadhaar?.reviewerRole}
                                                reason={customer?.kyc?.aadhaar?.reason}
                                            />
                                            <VerificationActions
                                                status={customer?.kyc?.aadhaar?.status}
                                                onVerify={() => aadhaarReviewVerify("verified")}
                                                onReject={() => aadhaarReviewVerify("rejected")}
                                                show={hasPermission("customer.kyc.aadhaar.review")}
                                                disabled={actionLoading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DetailCard>
                    ) : ""}

                    {/* Driving License Details */}
                    {customer?.kyc?.dl?.dlNo ? (
                        <DetailCard title="Driving License Details" icon={<FaIdCardAlt />}>
                            <div className="aadhaar-hero mb-4 p-4">
                                <div className="row align-items-center">
                                    {customer?.kyc?.dl?.dlData?.profile_image && (
                                        <div className="col-md-3 text-center mb-3 mb-md-0">
                                            <img
                                                src={`data:image/jpeg;base64,${customer?.kyc?.dl?.dlData?.profile_image}`}
                                                className="aadhaar-photo cursor-pointer"
                                                alt="Driving Licence"
                                                onClick={() => setLightbox({ src: `data:image/jpeg;base64,${customer?.kyc?.dl?.dlData?.profile_image}`, alt: "Driving licence photo" })}
                                            />
                                        </div>
                                    )}
                                    <div className="col-md-9" >
                                        <div className="d-flex justify-content-between align-items-start flex-wrap">
                                            <div>
                                                <h6 className="text-uppercase text-light opacity-75">
                                                    Driving License
                                                </h6>
                                                <h2 className="fw-bold text-white">{customer?.kyc?.dl?.dlData?.name}</h2>
                                                <p className="text-light mb-1">
                                                    S/o {customer?.kyc?.dl?.dlData?.father_or_husband_name}
                                                </p>

                                                <div className="mt-3">
                                                    <span className="dl-number-badge">
                                                        {customer?.kyc?.dl?.dlData?.license_number}
                                                    </span>
                                                </div>
                                                <div className="d-flex gap-4 mt-3 text-light small">
                                                    <span><strong>DOB:</strong> {customer?.kyc?.dl?.dlData?.dob}</span>
                                                    <span><strong>Issued:</strong> {customer?.kyc?.dl?.dlData?.doi}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <h4 className="section-title mb-4">Personal Information</h4>
                                <div className="info-grid px-3" >
                                    <div className="info-field">
                                        <div className="info-field-label"> Full Name </div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.name || "-"}</div>
                                    </div>
                                    <div className="info-field">
                                        <div className="info-field-label"> Father's Name </div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.father_or_husband_name || "-"}</div>
                                    </div>
                                    <div className="info-field">
                                        <div className="info-field-label"> Gender </div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.gender || "-"}</div>
                                    </div>
                                    <div className="info-field">
                                        <div className="info-field-label"> Date of Birth </div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.dob || "-"}</div>
                                    </div>
                                    <div className="info-field">
                                        <div className="info-field-label"> Blood Group </div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.blood_group || "-"}</div>
                                    </div>
                                    <div className="info-field">
                                        <div className="info-field-label"> RTO </div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.ola_name || "-"}</div>
                                    </div>
                                    <div className="info-field">
                                        <div className="info-field-label"> License Issuing State</div>
                                        <div className="info-value">{customer?.kyc?.dl?.dlData?.state || "-"}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="row" >
                                <div className="col-12 col-lg-5" >
                                    <div className="mb-2">
                                        <h4 className="section-title mb-4">Addresses</h4>
                                        <div className="address-display mb-4" >
                                            <h6>Permanent Address</h6>
                                            {customer?.kyc?.dl?.dlData?.permanent_address ? <p className="m-0">{customer?.kyc?.dl?.dlData?.permanent_address}</p> : <p className="m-0 text-muted">No address details available</p>}
                                        </div>
                                        <div className="address-display mb-4" >
                                            <h6>Temporary Address</h6>
                                            {customer?.kyc?.dl?.dlData?.temporary_address ? <p className="m-0">{customer?.kyc?.dl?.dlData?.temporary_address}</p> : <p className="m-0 text-muted">No address details available</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-lg-7" >
                                    <div className="mb-2">
                                        <h4 className="section-title mb-4">Class of Vehicles</h4>
                                        <div className="row">
                                            {customer?.kyc?.dl?.dlData?.vehicle_classes?.map((item, index) => (
                                                <div className="col-md-6 mb-4" key={index}>
                                                    <div className="vehicle-card p-4 h-100">
                                                        <div className="d-flex justify-content-between">
                                                            <h5 className="fw-bold">{item}</h5>
                                                            <span className="vehicle-icon">
                                                                {item === "LMV" ? <LiaCarSideSolid /> : <MdOutlineDirectionsBike />}
                                                            </span>
                                                        </div>
                                                        <hr />
                                                        <p>
                                                            <strong>Issue Date:</strong>{" "}
                                                            {customer?.kyc?.dl?.dlData?.doi}
                                                        </p>
                                                        <p>
                                                            <strong>Expiry Date:</strong>{" "}
                                                            {customer?.kyc?.dl?.dlData?.doe}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-4">
                                <div className="col-lg-7">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-white border-0 p-4 pb-2">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <h5 className="mb-1 fw-semibold">Driving License</h5>
                                                    <p className="text-muted small mb-0">
                                                        Submitted documents
                                                    </p>
                                                </div>
                                                <StatusPillLabel status={customer?.kyc?.dl?.status} />
                                            </div>
                                        </div>

                                        <div className="card-body p-4 pt-2">
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <DocFrame
                                                        label="Front Side"
                                                        src={customer?.kyc?.dl?.frontImageUrl}
                                                        alt="Driving license front"
                                                        onZoom={() => setLightbox({ src: customer.kyc.dl.frontImageUrl, alt: "Driving license front" })}
                                                        onOpen={() => openInNewTab(customer?.kyc?.dl?.frontImageUrl)}
                                                        missingText="No front image"
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <DocFrame
                                                        label="Back Side"
                                                        src={customer?.kyc?.dl?.backImageUrl}
                                                        alt="Driving license back"
                                                        onZoom={() => setLightbox({ src: customer.kyc.dl.backImageUrl, alt: "Driving license back" })}
                                                        onOpen={() => openInNewTab(customer?.kyc?.dl?.backImageUrl)}
                                                        missingText="No back image"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-5">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-white border-0 p-4 pb-2">
                                            <h5 className="mb-1 fw-semibold">Verification Details</h5>
                                            <p className="text-muted small mb-0">
                                                Review and verification information
                                            </p>
                                        </div>
                                        <div className="card-body p-4 pt-2">
                                            <VerificationDetails
                                                status={customer?.kyc?.dl?.status}
                                                submittedAt={customer?.kyc?.dl?.submittedAt}
                                                reviewedAt={customer?.kyc?.dl?.reviewedAt}
                                                reviewedBy={customer?.kyc?.dl?.reviewedBy}
                                                reviewerRole={customer?.kyc?.dl?.reviewerRole}
                                                reason={customer?.kyc?.dl?.reason}
                                            />
                                            <VerificationActions
                                                status={customer?.kyc?.dl?.status}
                                                onVerify={() => dlReviewVerify("verified")}
                                                onReject={() => dlReviewVerify("rejected")}
                                                show={hasPermission("customer.kyc.dl.review")}
                                                disabled={actionLoading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DetailCard>
                    ) : ""}

                    {/* Passport Details */}
                    {
                        customer?.kyc?.passport?.passportNo ? (
                            <DetailCard title="Passport Details" icon={<FaPassport />}>
                                <div className="mb-2">
                                    <div className="info-grid px-3" >
                                        <div className="info-field">
                                            <div className="info-field-label"> Full Name </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.full_name || "-"}</div>
                                        </div>
                                        <div className="info-field">
                                            <div className="info-field-label"> Date of Birth </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.dob || "-"}</div>
                                        </div>
                                        <div className="info-field">
                                            <div className="info-field-label"> Passport Number </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.passport_number || "-"}</div>
                                        </div>
                                        <div className="info-field">
                                            <div className="info-field-label"> File No </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.file_number || "-"}</div>
                                        </div>
                                        <div className="info-field">
                                            <div className="info-field-label"> Date of Application </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.date_of_application || "-"}</div>
                                        </div>
                                        <div className="info-field">
                                            <div className="info-field-label"> Application Type </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.application_type || "-"}</div>
                                        </div>
                                        <div className="info-field">
                                            <div className="info-field-label"> Application Status </div>
                                            <div className="info-value">{customer?.kyc?.passport?.passportData?.status || "-"}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm">
                                    <div className="card-body p-4">
                                        <h6 className="fw-semibold mb-3">Verification Details</h6>
                                        <VerificationDetails
                                            status={customer?.kyc?.passport?.status}
                                            submittedAt={customer?.kyc?.passport?.submittedAt}
                                            reviewedAt={customer?.kyc?.passport?.reviewedAt}
                                            reviewedBy={customer?.kyc?.passport?.reviewedBy}
                                            reviewerRole={customer?.kyc?.passport?.reviewerRole}
                                            reason={customer?.kyc?.passport?.reason}
                                        />
                                        <VerificationActions
                                            status={customer?.kyc?.passport?.status}
                                            onVerify={() => thirdDocumentReviewVerify("verified")}
                                            onReject={() => thirdDocumentReviewVerify("rejected")}
                                            show={hasPermission("customer.kyc.third.review")}
                                            disabled={actionLoading}
                                        />
                                    </div>
                                </div>
                            </DetailCard>
                        )
                            : ""
                    }

                    {/* Voter Details */}
                    {
                        customer?.kyc?.voter?.voterId ? (
                            <DetailCard title="Voter Details" icon={<FaIdCardAlt />}>
                                <div className="voter-hero p-4 mb-4">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <h4 className="fw-bold mb-1 text-uppercase">{customer?.kyc?.voter?.voterData?.name || "-"}</h4>
                                            <p className="text-muted mb-1">{customer?.kyc?.voter?.voterData?.nameInVernacular || "-"}</p>
                                            <div className="mt-2">
                                                <span className="epic-badge">
                                                    EPIC: {customer?.kyc?.voter?.voterData?.epicNo || "-"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                            <span className={`badge fs-6 px-3 py-2 ${customer?.kyc?.voter?.voterData?.status === "N" ? "bg-success" : "bg-danger"}`}>
                                                Electoral Roll: {customer?.kyc?.voter?.voterData?.status || "-"}
                                            </span>
                                            <div className="small mt-2">
                                                Last Updated: {customer?.kyc?.voter?.voterData?.lastUpdateDate || "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="info-card p-4 mb-4">
                                    <h6 className="section-title">Basic Information</h6>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <FieldRow label="Gender" value={customer?.kyc?.voter?.voterData?.gender} />
                                            <FieldRow label="Age" value={customer?.kyc?.voter?.voterData?.age} />
                                            <FieldRow label="Date of Birth" value={customer?.kyc?.voter?.voterData?.dateOfBirth} />
                                            <FieldRow label="Mobile" value={customer?.kyc?.voter?.voterData?.mobileNumber} />
                                            <FieldRow label="Email" value={customer?.kyc?.voter?.voterData?.emailId} />
                                        </div>
                                        <div className="col-md-6">
                                            <FieldRow label="District" value={customer?.kyc?.voter?.voterData?.district} />
                                            <FieldRow label="State" value={customer?.kyc?.voter?.voterData?.state} />
                                            <FieldRow label="State Code" value={customer?.kyc?.voter?.voterData?.stateCode} />
                                            <FieldRow label="ID" value={customer?.kyc?.voter?.voterData?.id} />
                                        </div>
                                    </div>
                                </div>

                                <div className="info-card p-4 mb-4">
                                    <h6 className="section-title">Family Details</h6>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <FieldRow label="Relative Name" value={customer?.kyc?.voter?.voterData?.relativeName} />
                                            <FieldRow label="Relative Name (Vernacular)" value={customer?.kyc?.voter?.voterData?.relativeNameInVernacular} />
                                        </div>
                                        <div className="col-md-6">
                                            <FieldRow label="Relation Type" value={customer?.kyc?.voter?.voterData?.relativeRelationType} />
                                            <FieldRow label="House Number" value={customer?.kyc?.voter?.voterData?.houseNumber} />
                                        </div>
                                    </div>
                                </div>

                                <div className="info-card p-4 mb-4">
                                    <h6 className="section-title">Constituency Details</h6>
                                    <FieldRow label="Parliamentary Constituency" value={customer?.kyc?.voter?.voterData?.parliamentaryConstituency} />
                                    <FieldRow label="Assembly Constituency" value={customer?.kyc?.voter?.voterData?.assemblyConstituency} />
                                    <FieldRow label="Section of Constituency Part" value={customer?.kyc?.voter?.voterData?.sectionOfConstituencyPart} />
                                    <FieldRow label="Part Number / Location Number" value={customer?.kyc?.voter?.voterData?.partNumberOrLocationNumberInConstituency} />
                                    <FieldRow label="Card Serial Number" value={customer?.kyc?.voter?.voterData?.cardSerialNumberInPollingList} />
                                    <FieldRow label="Part / Location Description" value={customer?.kyc?.voter?.voterData?.partOrLocationInConstituency} />
                                </div>

                                <div className="info-card p-4 mb-4">
                                    <h6 className="section-title">Polling Booth Details</h6>
                                    <FieldRow label="Booth Name" value={customer?.kyc?.voter?.voterData?.pollingBoothDetails?.name} />
                                    <FieldRow label="Booth Name (Vernacular)" value={customer?.kyc?.voter?.voterData?.pollingBoothDetails?.nameVernacular} />
                                    <FieldRow label="Booth Number" value={customer?.kyc?.voter?.voterData?.pollingBoothDetails?.number} />
                                    <FieldRow label="Latitude / Longitude" value={customer?.kyc?.voter?.voterData?.pollingBoothDetails?.latLong} />
                                    <FieldRow label="Booth Coordinates" value={customer?.kyc?.voter?.voterData?.pollingBoothCoordinates} />
                                    <FieldRow label="Booth Address" value={customer?.kyc?.voter?.voterData?.pollingBoothAddress} />
                                    <FieldRow label="Booth Number (Alt)" value={customer?.kyc?.voter?.voterData?.pollingBoothNumber} />
                                </div>

                                <div className="row" >
                                    <div className="col-12 col-lg-7" >
                                        <div className="info-card p-4 mb-4">
                                            <h6 className="section-title">Address Details</h6>
                                            <FieldRow label="District Code" value={customer?.kyc?.voter?.voterData?.address?.districtCode} />
                                            <FieldRow label="District Name" value={customer?.kyc?.voter?.voterData?.address?.districtName} />
                                            <FieldRow label="District Name (Vernacular)" value={customer?.kyc?.voter?.voterData?.address?.districtNameVernacular} />
                                            <FieldRow label="State" value={customer?.kyc?.voter?.voterData?.address?.state} />
                                            <FieldRow label="State Code" value={customer?.kyc?.voter?.voterData?.address?.stateCode} />
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-5" >
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-body p-4">
                                                <h6 className="fw-semibold mb-3">Verification Details</h6>
                                                <VerificationDetails
                                                    status={customer?.kyc?.voter?.status}
                                                    submittedAt={customer?.kyc?.voter?.submittedAt}
                                                    reviewedAt={customer?.kyc?.voter?.reviewedAt}
                                                    reviewedBy={customer?.kyc?.voter?.reviewedBy}
                                                    reviewerRole={customer?.kyc?.voter?.reviewerRole}
                                                    reason={customer?.kyc?.voter?.reason}
                                                />
                                                <VerificationActions
                                                    status={customer?.kyc?.voter?.status}
                                                    onVerify={() => thirdDocumentReviewVerify("verified")}
                                                    onReject={() => thirdDocumentReviewVerify("rejected")}
                                                    show={hasPermission("customer.kyc.third.review")}
                                                    disabled={actionLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </DetailCard>
                        ) : ""
                    }

                    <KycReviewPanel customerId={customerId} customer={customer} basePath="/staff/customer" panel="staff" canOverride={hasPermission("kyc.override")} canResetTries={hasPermission("kyc.attempts.reset")} onChanged={load} />

                    <KycLogPanel customerId={customerId} basePath="/staff/customer" refreshKey={customer?.updatedAt || ""} />

                    <TestModeCard customerId={customerId} customer={customer} basePath="/staff/customer" />

                    <LinkedCustomerCard customerId={customerId} customer={customer} canLink={hasPermission("customer.edit")} />

                    {hasPermission("booking.view") ? <CustomerBookings customerId={customerId} /> : ""}
                </div>
                <div className="col-12 col-xl-4 mt-xl-0 mt-3">
                    {hasPermission("customer.timeline") ? (
                        <Timeline userLog={logs} />
                    ) : (
                        <div className="card border-0 shadow-sm rounded-4 p-4 text-center text-muted">
                            <FaBan className="mx-auto mb-2" size={18} />
                            <div className="small">You don't have permission to view the activity timeline.</div>
                        </div>
                    )}
                </div>
            </div >

            <CustomerVerification customerId={customerId} stage={stage} customer={customer} onCustomerUpdate={load} />

            {
                showNameGender ? (
                    <NameGenderModal
                        customer={customer}
                        onClose={() => setShowNameGender(false)}
                        onSaved={() => { setShowNameGender(false); load(); }}
                    />
                ) : ""
            }

            {
                rejectPrompt ? (
                    <RejectReasonModal
                        title={rejectPrompt.title}
                        onClose={() => setRejectPrompt(null)}
                        onSubmit={async (reason) => {
                            await rejectPrompt.onSubmit(reason);
                            setRejectPrompt(null);
                        }}
                    />
                ) : null
            }

            {
                activateOpen ? (
                    <AcceptModal
                        confirmLabel="Reactivate"
                        onClose={() => setActivateOpen(false)}
                        onSubmit={() => changeStatus(true, "Reactivated by staff")}
                    />
                ) : null
            }

            {
                lightbox ? (
                    <div className="cv-lightbox" role="dialog" aria-modal="true" aria-label={lightbox.alt} onClick={() => setLightbox(null)}>
                        <button type="button" className="cv-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close preview">
                            <FaTimesCircle />
                        </button>
                        <img src={lightbox.src} alt={lightbox.alt} onClick={(e) => e.stopPropagation()} />
                    </div>
                ) : null
            }
        </>
    );
};

export default CustomerDetail;
